// frontend/src/components/WeightHistoryView.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { BACKEND_URL } from "../config";

function WeightHistoryView({ userId, token }) {
  const [weightData, setWeightData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(90);

  useEffect(() => {
    loadWeightHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, userId, token]);

  const loadWeightHistory = async () => {
    if (!userId || !token) return;
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/weight/history/${userId}?days=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setWeightData(data);
      } else {
        const txt = await response.text();
        console.error("Error loading weight history:", txt);
      }
    } catch (error) {
      console.error("Error loading weight history:", error);
    }
    setLoading(false);
  };

  // --- helpers for "new" Withings data but backward-compatible ---
  const getField = (log, key) => {
    if (!log) return null;
    // Prefer top-level (your Withings upsert can store at top-level),
    // fallback to log.extra (your schema supports that).
    if (log[key] !== undefined) return log[key];
    if (log.extra && log.extra[key] !== undefined) return log.extra[key];
    return null;
  };


  const getAny = (log, keys) => {
    for (const k of keys) {
      const v = getField(log, k);
      if (v !== null && v !== undefined && v !== "") return v;
    }
    return null;
  };
  const fmtMaybe = (v, digits = 1) => {
    if (v === null || v === undefined || v === "") return "-";
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return n.toFixed(digits);
  };

  const fmtDateLabel = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
      return String(iso || "");
    }
  };

  const fmtDateLong = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    } catch {
      return String(iso || "");
    }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return String(iso);
    }
  };

  const chartData = useMemo(() => {
    if (!weightData?.logs?.length) return [];

    // Reverse to show oldest first (preserves your prior behavior)
    return [...weightData.logs]
      .reverse()
      .map((log) => {
        const fatRatio = getField(log, "fat_ratio"); // percent
        const bmi = getField(log, "bmi");
        const source = log.source || getField(log, "source") || "manual";
        const measuredAt = log.measured_at || getField(log, "measured_at") || null;

        return {
          date: fmtDateLabel(log.date),
          fullDate: log.date,
          measured_at: measuredAt,
          source,
          weight: Number(getAny(log, ["weight_lbs","weight"]) ) || 0,
          notes: log.notes,
          fat_ratio: fatRatio !== null && fatRatio !== undefined ? Number(fatRatio) : null,
          bmi: bmi !== null && bmi !== undefined ? Number(bmi) : null,
          fat_mass_lbs: getAny(log, ["fat_mass_lbs","fat_mass"]) != null ? Number(getAny(log, ["fat_mass_lbs","fat_mass"])) : null,
          fat_free_mass_lbs: getAny(log, ["fat_free_mass_lbs","fat_free_mass"]) != null ? Number(getAny(log, ["fat_free_mass_lbs","fat_free_mass"])) : null,
          muscle_mass_lbs: getAny(log, ["muscle_mass_lbs","muscle_mass"]) != null ? Number(getAny(log, ["muscle_mass_lbs","muscle_mass"])) : null,
          bone_mass_lbs: getAny(log, ["bone_mass_lbs","bone_mass"]) != null ? Number(getAny(log, ["bone_mass_lbs","bone_mass"])) : null,

          systolic: getAny(log, ["systolic"]) != null ? Number(getAny(log, ["systolic"])) : null,
          diastolic: getAny(log, ["diastolic"]) != null ? Number(getAny(log, ["diastolic"])) : null,
          heart_rate: getAny(log, ["heart_rate"]) != null ? Number(getAny(log, ["heart_rate"])) : null,
          hydration_percent: getAny(log, ["hydration_percent"]) != null ? Number(getAny(log, ["hydration_percent"])) : null,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightData]);

  const progress = useMemo(() => {
    if (!weightData?.starting_weight || !weightData?.current_weight) return null;

    const change = weightData.current_weight - weightData.starting_weight;
    const percentChange = ((change / weightData.starting_weight) * 100).toFixed(1);

    let toGoal = null;
    if (weightData.goal_weight) toGoal = weightData.current_weight - weightData.goal_weight;

    return { change, percentChange, toGoal };
  }, [weightData]);

  // latest-extra metrics (optional cards; does NOT break old UI)
  const latestExtras = useMemo(() => {
    const logs = weightData?.logs || [];
    if (!logs.length) return null;

    // logs come in newest-first from backend in your current UI usage
    const latest = logs[0];
    const fatRatio = getAny(latest, ["fat_ratio_percent", "fat_ratio"]);
    const bmi = getField(latest, "bmi");
    const systolic = getField(latest, "systolic");
    const diastolic = getField(latest, "diastolic");
    const hydrationPercent = getField(latest, "hydration_percent");
    const heartRate = getField(latest, "heart_rate");
    const fatMass = getAny(latest, ["fat_mass_lbs", "fat_mass"]);
    const fatFreeMass = getAny(latest, ["fat_free_mass_lbs", "fat_free_mass"]);
    const muscleMass = getAny(latest, ["muscle_mass_lbs", "muscle_mass"]);
    const boneMass = getAny(latest, ["bone_mass_lbs", "bone_mass"]);

    return {
      fatRatio,
      fatMass,
      fatFreeMass,
      muscleMass,
      boneMass,
      bmi,
      systolic,
      diastolic,
      heartRate,
      hydrationPercent,
      source: latest?.source || "manual",
      measuredAt: latest?.measured_at || null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload || {};
    return (
      <div
        style={{
          background: "var(--bg-modal)",
          border: "1px solid var(--border-light)",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          padding: "10px 12px",
          minWidth: 180,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)", marginBottom: 6 }}>
          {label || p.date}
        </div>

        <div style={{ fontSize: 12, color: "var(--text-secondary)", display: "grid", gap: 4 }}>
          <div>
            <span style={{ color: "var(--text-muted)" }}>Weight:</span>{" "}
            <span style={{ fontWeight: 700 }}>{fmtMaybe(p.weight, 1)} lbs</span>
          </div>

          {/* Optional */}
          {p.fat_ratio !== null && p.fat_ratio !== undefined && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>Body Fat:</span>{" "}
              <span style={{ fontWeight: 700 }}>{fmtMaybe(p.fat_ratio, 1)}%</span>
            </div>
          )}
          
          {p.fat_mass_lbs != null && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>Fat Mass:</span>{" "}
              <span style={{ fontWeight: 700 }}>{fmtMaybe(p.fat_mass_lbs, 1)} lbs</span>
            </div>
          )}
          {p.fat_free_mass_lbs != null && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>Fat Free:</span>{" "}
              <span style={{ fontWeight: 700 }}>{fmtMaybe(p.fat_free_mass_lbs, 1)} lbs</span>
            </div>
          )}
          {p.muscle_mass_lbs != null && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>Muscle:</span>{" "}
              <span style={{ fontWeight: 700 }}>{fmtMaybe(p.muscle_mass_lbs, 1)} lbs</span>
            </div>
          )}
          {p.bone_mass_lbs != null && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>Bone:</span>{" "}
              <span style={{ fontWeight: 700 }}>{fmtMaybe(p.bone_mass_lbs, 1)} lbs</span>
            </div>
          )}

          {(p.systolic != null || p.diastolic != null) && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>BP:</span>{" "}
              <span style={{ fontWeight: 700 }}>
                {p.systolic != null ? Math.round(p.systolic) : "—"}/{p.diastolic != null ? Math.round(p.diastolic) : "—"}
              </span>
            </div>
          )}

          {p.heart_rate != null && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>Heart rate:</span>{" "}
              <span style={{ fontWeight: 700 }}>{fmtMaybe(p.heart_rate, 0)} bpm</span>
            </div>
          )}

          {p.hydration_percent != null && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>Hydration:</span>{" "}
              <span style={{ fontWeight: 700 }}>{fmtMaybe(p.hydration_percent, 1)}%</span>
            </div>
          )}


          {p.bmi !== null && p.bmi !== undefined && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>BMI:</span>{" "}
              <span style={{ fontWeight: 700 }}>{fmtMaybe(p.bmi, 1)}</span>
            </div>
          )}

          <div>
            <span style={{ color: "var(--text-muted)" }}>Source:</span>{" "}
            <span style={{ fontWeight: 700 }}>{(p.source || "manual").toString()}</span>
          </div>

          {p.measured_at && (
            <div>
              <span style={{ color: "var(--text-muted)" }}>Measured:</span>{" "}
              <span style={{ fontWeight: 600 }}>{fmtDateTime(p.measured_at)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="card" style={{ padding: "32px", textAlign: "center" }}>
        <p>Loading weight history...</p>
      </div>
    );
  }

  return (
    <div className="weight-history" data-testid="weight-history">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h2 className="heading-3" style={{ margin: 0 }}>
          Health History
        </h2>
        <div style={{ display: "flex", gap: "8px" }}>
          {[30, 90, 180, 365].map((days) => (
            <button
              key={days}
              onClick={() => setDateRange(days)}
              style={{
                padding: "6px 14px",
                fontSize: "13px",
                fontWeight: 500,
                borderRadius: "20px",
                border: "none",
                cursor: "pointer",
                transition: "all 0.2s",
                background: dateRange === days ? "var(--text-primary)" : "var(--bg-section)",
                color: dateRange === days ? "#fff" : "var(--text-secondary)",
              }}
            >
              {days === 365 ? "1Y" : `${days}D`}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards (existing + optional extras) */}
      {weightData && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          <div
            className="card"
            style={{
              padding: "16px",
              textAlign: "center",
              background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
            }}
          >
            <div style={{ fontSize: "12px", color: "#0369a1", fontWeight: 500, marginBottom: "4px" }}>Current</div>
            <div style={{ fontSize: "24px", fontWeight: 700, color: "#0c4a6e" }}>
              {weightData.current_weight?.toFixed(1) || "-"}
            </div>
            <div style={{ fontSize: "11px", color: "#0369a1" }}>lbs</div>
          </div>

          {weightData.goal_weight && (
            <div
              className="card"
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
              }}
            >
              <div style={{ fontSize: "12px", color: "#15803d", fontWeight: 500, marginBottom: "4px" }}>Goal</div>
              <div style={{ fontSize: "24px", fontWeight: 700, color: "#14532d" }}>{weightData.goal_weight?.toFixed(1)}</div>
              <div style={{ fontSize: "11px", color: "#15803d" }}>lbs</div>
            </div>
          )}

          {progress && (
            <>
              <div
                className="card"
                style={{
                  padding: "16px",
                  textAlign: "center",
                  background:
                    progress.change <= 0
                      ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
                      : "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: progress.change <= 0 ? "#15803d" : "#dc2626",
                    fontWeight: 500,
                    marginBottom: "4px",
                  }}
                >
                  Change
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: 700,
                    color: progress.change <= 0 ? "#14532d" : "#991b1b",
                  }}
                >
                  {progress.change > 0 ? "+" : ""}
                  {progress.change.toFixed(1)}
                </div>
                <div style={{ fontSize: "11px", color: progress.change <= 0 ? "#15803d" : "#dc2626" }}>
                  lbs ({progress.percentChange}%)
                </div>
              </div>

              {progress.toGoal !== null && (
                <div
                  className="card"
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
                  }}
                >
                  <div style={{ fontSize: "12px", color: "#7c3aed", fontWeight: 500, marginBottom: "4px" }}>To Goal</div>
                  <div style={{ fontSize: "24px", fontWeight: 700, color: "#5b21b6" }}>{progress.toGoal.toFixed(1)}</div>
                  <div style={{ fontSize: "11px", color: "#7c3aed" }}>lbs to go</div>
                </div>
              )}
            </>
          )}

          {/* Optional metric cards (only if present; keeps old UI intact) */}
          {latestExtras?.fatRatio !== null && latestExtras?.fatRatio !== undefined && (
            <div
              className="card"
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
              }}
            >
              <div style={{ fontSize: "12px", color: "#9a3412", fontWeight: 500, marginBottom: "4px" }}>Body Fat</div>
              <div style={{ fontSize: "24px", fontWeight: 700, color: "#7c2d12" }}>{fmtMaybe(latestExtras.fatRatio, 1)}</div>
              <div style={{ fontSize: "11px", color: "#9a3412" }}>%</div>
            </div>
          )}

          {latestExtras?.bmi !== null && latestExtras?.bmi !== undefined && (
            <div
              className="card"
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
              }}
            >
              <div style={{ fontSize: "12px", color: "#6d28d9", fontWeight: 500, marginBottom: "4px" }}>BMI</div>
              <div style={{ fontSize: "24px", fontWeight: 700, color: "#4c1d95" }}>{fmtMaybe(latestExtras.bmi, 1)}</div>
              <div style={{ fontSize: "11px", color: "#6d28d9" }}>index</div>
            </div>
          )}

          {latestExtras?.muscleMass !== null && latestExtras?.muscleMass !== undefined && (
            <div
              className="card"
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, rgba(0,128,255,0.10) 0%, rgba(0,128,255,0.04) 100%)",
              }}
            >
              <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontWeight: 700, marginBottom: "4px" }}>
                Muscle Mass
              </div>
              <div style={{ fontSize: "24px", fontWeight: 800, color: "var(--text-primary)" }}>
                {fmtMaybe(latestExtras.muscleMass, 1)}
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>lbs</div>
            </div>
          )}

          {latestExtras?.hydrationPercent !== null && latestExtras?.hydrationPercent !== undefined && (
            <div
              className="card"
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, rgba(88,80,236,0.10) 0%, rgba(88,80,236,0.04) 100%)",
              }}
            >
              <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontWeight: 700, marginBottom: "4px" }}>
                Hydration
              </div>
              <div style={{ fontSize: "24px", fontWeight: 800, color: "var(--text-primary)" }}>
                {fmtMaybe(latestExtras.hydrationPercent, 1)}
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>%</div>
            </div>
          )}

          {(latestExtras?.systolic !== null || latestExtras?.diastolic !== null) && (
            <div
              className="card"
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(245,158,11,0.04) 100%)",
              }}
            >
              <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontWeight: 700, marginBottom: "4px" }}>
                Blood Pressure
              </div>
              <div style={{ fontSize: "24px", fontWeight: 800, color: "var(--text-primary)" }}>
                {latestExtras?.systolic != null ? Math.round(Number(latestExtras.systolic)) : "—"}/
                {latestExtras?.diastolic != null ? Math.round(Number(latestExtras.diastolic)) : "—"}
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>mmHg</div>
            </div>
          )}

          {latestExtras?.heartRate !== null && latestExtras?.heartRate !== undefined && (
            <div
              className="card"
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, rgba(220,38,38,0.10) 0%, rgba(220,38,38,0.04) 100%)",
              }}
            >
              <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontWeight: 700, marginBottom: "4px" }}>
                Heart Rate
              </div>
              <div style={{ fontSize: "24px", fontWeight: 800, color: "var(--text-primary)" }}>
                {fmtMaybe(latestExtras.heartRate, 0)}
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>bpm</div>
            </div>
          )}

  
        </div>
      )}

      {/* Weight Chart (unchanged, but tooltip enhanced) */}
      {chartData.length > 0 ? (
        <div className="card" style={{ padding: "20px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "var(--text-primary)" }}>Weight Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                dx={-10}
                domain={["dataMin - 5", "dataMax + 5"]}
              />
              <Tooltip content={<CustomTooltip />} />
              {weightData?.goal_weight && (
                <ReferenceLine
                  y={weightData.goal_weight}
                  stroke="#10b981"
                  strokeDasharray="5 5"
                  label={{ value: "Goal", position: "right", fill: "#10b981", fontSize: 11 }}
                />
              )}
              <Line
                type="monotone"
                dataKey="weight"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: "#3b82f6" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="card" style={{ textAlign: "center", padding: "48px" }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚖️</div>
          <p className="body-medium" style={{ color: "var(--text-secondary)" }}>
            No weight entries yet. Log your first weigh-in to start tracking!
          </p>
        </div>
      )}

      {/* Weight Log Table (extended but backward compatible) */}
      {weightData?.logs?.length > 0 && (
        <div className="card" style={{ marginTop: "16px", padding: "20px" }}>
          <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px", color: "var(--text-primary)" }}>Recent Entries</h3>

          <div className="ct-table-wrap">
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border-light)" }}>
                  <th style={{ textAlign: "left", padding: "12px 8px", fontWeight: 600 }}>Date</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Weight</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Body Fat %</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Fat Mass</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Fat Free</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Bone</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>BMI</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Muscle</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Hydration</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>Heart Rate</th>
                  <th style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>BP</th>
                  <th style={{ textAlign: "left", padding: "12px 8px", fontWeight: 600 }}>Source</th>
                  <th style={{ textAlign: "left", padding: "12px 8px", fontWeight: 600 }}>Measured</th>
                  <th style={{ textAlign: "left", padding: "12px 8px", fontWeight: 600 }}>Notes</th>
                </tr>
              </thead>

              <tbody>
                {weightData.logs.slice(0, 10).map((log, index) => {
                  const fatRatio = getAny(log, ["fat_ratio_percent", "fat_ratio"]);
                  const bmi = getField(log, "bmi");
                  const muscle = getAny(log, ["muscle_mass_lbs", "muscle_mass"]); // lbs
                  const hydration = getField(log, "hydration_percent"); // percent
                  const sys = getField(log, "systolic");
                  const dia = getField(log, "diastolic");

                  const src = log.source || "manual";
                  const measuredAt = log.measured_at || null;

                  return (
                    <tr key={log.log_id || log._id || `${log.date}-${src}-${index}`} style={{ borderBottom: "1px solid var(--border-light)" }}>
                      <td style={{ padding: "12px 8px" }}>{fmtDateLong(log.date)}</td>

                      <td style={{ textAlign: "right", padding: "12px 8px", fontWeight: 600 }}>
                        {fmtMaybe(getAny(log, ["weight_lbs","weight"]), 1)} lbs
                      </td>

                      <td style={{ textAlign: "right", padding: "12px 8px", color: "var(--text-secondary)" }}>
                        {fatRatio !== null && fatRatio !== undefined ? `${fmtMaybe(fatRatio, 1)}%` : "-"}
                      </td>

                      <td style={{ textAlign: "right", padding: "12px 8px", color: "var(--text-secondary)" }}>
                        {getAny(log, ["fat_mass_lbs","fat_mass"]) != null ? `${fmtMaybe(getAny(log, ["fat_mass_lbs","fat_mass"]), 1)} lbs` : "-"}
                      </td>

                      <td style={{ textAlign: "right", padding: "12px 8px", color: "var(--text-secondary)" }}>
                        {getAny(log, ["fat_free_mass_lbs","fat_free_mass"]) != null ? `${fmtMaybe(getAny(log, ["fat_free_mass_lbs","fat_free_mass"]), 1)} lbs` : "-"}
                      </td>

                      <td style={{ textAlign: "right", padding: "12px 8px", color: "var(--text-secondary)" }}>
                        {getAny(log, ["bone_mass_lbs","bone_mass"]) != null ? `${fmtMaybe(getAny(log, ["bone_mass_lbs","bone_mass"]), 1)} lbs` : "-"}
                      </td>

                      <td style={{ textAlign: "right", padding: "12px 8px", color: "var(--text-secondary)" }}>
                        {bmi !== null && bmi !== undefined ? fmtMaybe(bmi, 1) : "-"}
                      </td>

                      <td style={{ textAlign: "right", padding: "12px 8px", color: "var(--text-secondary)" }}>
                        {muscle !== null && muscle !== undefined ? `${fmtMaybe(muscle, 1)} lbs` : "-"}
                      </td>

                      <td style={{ textAlign: "right", padding: "12px 8px", color: "var(--text-secondary)" }}>
                        {hydration !== null && hydration !== undefined ? `${fmtMaybe(hydration, 1)}%` : "-"}
                      </td>

                      <td style={{ textAlign: "right", padding: "12px 8px", color: "var(--text-secondary)" }}>
                        {getField(log, "heart_rate") != null ? `${fmtMaybe(getField(log, "heart_rate"), 0)} bpm` : "-"}
                      </td>

                      <td style={{ textAlign: "right", padding: "12px 8px", color: "var(--text-secondary)" }}>
                        {sys && dia ? `${sys}/${dia}` : "-"}
                      </td>

                      <td style={{ padding: "12px 8px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 700,
                            background: src === "withings" ? "rgba(0,128,255,0.12)" : "var(--bg-section)",
                            color: src === "withings" ? "var(--brand-primary)" : "var(--text-primary)",
                            textTransform: "capitalize",
                          }}
                        >
                          {src.replace("_", " ")}
                        </span>
                      </td>

                      <td style={{ padding: "12px 8px", color: "var(--text-secondary)" }}>{fmtDateTime(measuredAt)}</td>

                      <td style={{ padding: "12px 8px", color: "var(--text-secondary)" }}>{log.notes || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-muted)" }}>
            Tip: Withings entries often include extra metrics (fat %, BMI, etc). Manual entries may show only weight/notes.
          </div>
        </div>
      )}
    </div>
  );
}

export default WeightHistoryView;
